import { setup, assign } from "xstate"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Type de source sélectionné (PDL, PCE ou capteur)
 */
export type SourceTypeChoice = "pdl" | "pce" | "source"

/**
 * Input pour initialiser la modal d'ajout de source
 */
export interface SourceAddModalInput {
    /**
     * État initial (redirection directe depuis l'entrée)
     * Si défini, la modal affiche directement le parcours correspondant.
     */
    fromState?: SourceTypeChoice
    /**
     * Types de source proposés (sélecteur + accès capteur). Si défini sans `source`, le parcours capteur est exclu.
     */
    allowedSourceTypes?: SourceTypeChoice[]
    /**
     * Identifiant du bâtiment (pour PDL, PCE et capteur)
     */
    buildingId?: string
    /**
     * Adresse du bâtiment (pour préremplissage capteur)
     */
    address?: Record<string, unknown>
    /**
     * Quand true, la machine se ferme après un formulaire validé.
     * Quand false, elle revient au sélecteur de type.
     */
    closeOnDone?: boolean
}

/**
 * Contexte de la machine de la modal d'ajout de source
 */
export interface SourceAddModalContext {
    input: SourceAddModalInput
}

/**
 * Événements de la machine
 */
export type SourceAddModalEvent =
    | { type: "SUBMIT"; sourceType: SourceTypeChoice }
    | { type: "BACK" }
    | { type: "UPDATE_FORM_DONE" }
    | { type: "CANCEL" }

// ================================
// MACHINE FACTORY
// ================================

/**
 * Crée la machine à états pour la modal d'ajout de source (choix type + orchestration PDL / PCE / Capteur).
 * Correspond à la migration de ModalSourceFactory (legacy).
 *
 * @param input - Input initial (fromState, buildingId, address)
 * @returns Machine XState configurée
 */
export function createSourceAddModalMachine(input: SourceAddModalInput = {}) {
    return setup({
        types: {
            input: {} as SourceAddModalInput,
            context: {} as SourceAddModalContext,
            events: {} as SourceAddModalEvent,
        },
        actions: {
            setInput: assign({
                input: ({ context }, params: { input: SourceAddModalInput }) =>
                    params?.input ?? context.input,
            }),
        },
    }).createMachine({
        id: "source-append-modal",
        types: {} as {
            context: SourceAddModalContext
            events: SourceAddModalEvent
        },
        context: {
            input: input || {},
        },
        initial: "idle",
        states: {
            idle: {
                always: [
                    {
                        target: "PdlForm",
                        guard: ({ context }) =>
                            context.input?.fromState === "pdl",
                    },
                    {
                        target: "PceForm",
                        guard: ({ context }) =>
                            context.input?.fromState === "pce",
                    },
                    {
                        target: "SourceAppendFormTypeSelector",
                        guard: ({ context }) =>
                            context.input?.fromState === "source" &&
                            Array.isArray(context.input?.allowedSourceTypes) &&
                            context.input.allowedSourceTypes.length > 0 &&
                            !context.input.allowedSourceTypes.includes(
                                "source"
                            ),
                    },
                    {
                        target: "SensorForm",
                        guard: ({ context }) =>
                            context.input?.fromState === "source" &&
                            (!context.input?.allowedSourceTypes?.length ||
                                context.input.allowedSourceTypes.includes(
                                    "source"
                                )),
                    },
                    {
                        target: "SourceAppendFormTypeSelector",
                    },
                ],
            },
            SourceAppendFormTypeSelector: {
                on: {
                    SUBMIT: [
                        {
                            target: "PdlForm",
                            guard: ({ event }) =>
                                event.type === "SUBMIT" &&
                                event.sourceType === "pdl",
                        },
                        {
                            target: "PceForm",
                            guard: ({ event }) =>
                                event.type === "SUBMIT" &&
                                event.sourceType === "pce",
                        },
                        {
                            target: "SensorForm",
                            guard: ({ event, context }) =>
                                event.type === "SUBMIT" &&
                                event.sourceType === "source" &&
                                (!context.input?.allowedSourceTypes?.length ||
                                    context.input.allowedSourceTypes.includes(
                                        "source"
                                    )),
                        },
                    ],
                    CANCEL: "closed",
                },
            },
            PdlForm: {
                on: {
                    BACK: "SourceAppendFormTypeSelector",
                    UPDATE_FORM_DONE: [
                        {
                            target: "closed",
                            guard: ({ context }) =>
                                context.input?.closeOnDone !== false,
                        },
                        { target: "SourceAppendFormTypeSelector" },
                    ],
                    CANCEL: "closed",
                },
            },
            PceForm: {
                on: {
                    BACK: "SourceAppendFormTypeSelector",
                    UPDATE_FORM_DONE: [
                        {
                            target: "closed",
                            guard: ({ context }) =>
                                context.input?.closeOnDone !== false,
                        },
                        { target: "SourceAppendFormTypeSelector" },
                    ],
                    CANCEL: "closed",
                },
            },
            SensorForm: {
                on: {
                    BACK: "SourceAppendFormTypeSelector",
                    UPDATE_FORM_DONE: [
                        {
                            target: "closed",
                            guard: ({ context }) =>
                                context.input?.closeOnDone !== false,
                        },
                        { target: "SourceAppendFormTypeSelector" },
                    ],
                    CANCEL: "closed",
                },
            },
            closed: {
                type: "final",
            },
        },
    })
}

export type SourceAddModalMachine = ReturnType<
    typeof createSourceAddModalMachine
>
